import GroupTripPartyAdditionSection from './GroupTripPartyAdditionSection';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { groupTripApi } from '../../api/groupTripApi';
import {
  canHostEditGroupTrip,
  canRevokeApprovedApplication,
  confirmPayload,
  groupTripDateConflicts,
  groupTripErrorMessage,
  canHostCancelGroupTrip,
  canHostRemoveParticipant,
  hostCancelActionLabel,
  hostCancellationNotice,
  isGroupTripCapacityExceededError,
  isGroupTripDateConflictError,
  isGroupTripDateConflictRetryable,
  isGroupTripOtherMemberDateConflictError,
  isHostApproveBlockedByCapacity,
  normalizeApplicationList,
  normalizeGroupTrip,
  normalizeParticipantList,
  normalizeTravelerList,
  occupancyCopy,
  opaqueId,
  paidParticipantHostHint,
  tripPhaseCopy,
} from './groupTripModel';
import { lateJoinTravelerNote } from './groupTripLateJoinModel';
import {
  groupTripApplicationStatusLabel,
  groupTripParticipantStatusLabel,
  groupTripRecruitmentStatusLabel,
} from './groupTripPresentation';
import { groupTripMemberFacingLabel } from '../member-identity/memberIdentityPresentation';
import GroupTripActivityParticipation from './GroupTripActivityParticipation';
import GroupTripD1Participation from './GroupTripD1Participation';
import GroupTripRuntimeDecisions from './GroupTripRuntimeDecisions';
import GroupTripRuntimeTimeline from './GroupTripRuntimeTimeline';
import GroupTripLateJoinSection from './GroupTripLateJoinSection';
import MemberSafetyActions from './MemberSafetyActions';
import GroupTripChatButton from './GroupTripChatButton';
import GroupTripDateConflictNotice from './GroupTripDateConflictNotice';
import { ConfirmDialog } from '../../components/ui';
import './group-trip.css';

export default function HostGroupTripDetailPage() {
  const { groupTripId } = useParams();
  const [searchParams] = useSearchParams();
  const focusApplicationId = opaqueId(searchParams.get('applicationId'));
  const { t, tf } = useTravelTranslation();
  const { user } = useAuth();
  const viewerId = user?.memberId ?? user?.id;
  const [trip, setTrip] = useState(null);
  const [applications, setApplications] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [travelers, setTravelers] = useState([]);
  const [issuedClaim, setIssuedClaim] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [dateConflict, setDateConflict] = useState(null);
  const [busy, setBusy] = useState('');
  const [focusMissed, setFocusMissed] = useState(false);
  /** @type {[{ kind: 'cancel' } | { kind: 'remove', participantId: string } | { kind: 'reject', applicationId: string } | { kind: 'revoke', applicationId: string } | null, Function]} */
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const generation = useRef(0);
  const focusedCardRef = useRef(null);

  const load = useCallback(async () => {
    const token = ++generation.current;
    setDateConflict(null);
    if (!/^[1-9]\d*$/.test(groupTripId || '')) {
      setPhase('error');
      setError(t('travel.groupTrip.notFound'));
      return;
    }
    setPhase('loading'); setError('');
    try {
      const detail = normalizeGroupTrip(await groupTripApi.get(groupTripId), t);
      const list = normalizeApplicationList(await groupTripApi.listApplications(groupTripId));
      const memberList = normalizeParticipantList(await groupTripApi.listParticipants(groupTripId));
      let travelerItems = [];
      try {
        travelerItems = normalizeTravelerList(await groupTripApi.listTravelers(groupTripId)).items;
      } catch {
        travelerItems = [];
      }
      if (token !== generation.current) return;
      setTrip(detail);
      setApplications(list.items);
      setParticipants(memberList.items);
      setTravelers(travelerItems);
      if (focusApplicationId) {
        const found = list.items.some(
          (item) => String(item.applicationId) === String(focusApplicationId),
        );
        setFocusMissed(!found);
      } else {
        setFocusMissed(false);
      }
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(groupTripErrorMessage(cause, t));
      setPhase('error');
    }
  }, [groupTripId, focusApplicationId, t]);

  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);

  useEffect(() => {
    if (phase !== 'ready' || !focusApplicationId || focusMissed) return undefined;
    const node = focusedCardRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return undefined;
  }, [phase, focusApplicationId, focusMissed, applications]);

  async function run(kind, action) {
    if (!trip || busy) return;
    setBusy(kind); setError(''); setDateConflict(null);
    try {
      await action();
      await load();
      setPendingConfirm(null);
    } catch (cause) {
      if (isGroupTripOtherMemberDateConflictError(cause)) {
        setDateConflict({ variant: 'applicant' });
        return;
      }
      if (isGroupTripDateConflictError(cause)) {
        setDateConflict(hostDateConflictNotice(kind, cause));
        return;
      }
      const message = groupTripErrorMessage(cause, t, 'travel.common.actionFailed');
      // Capacity race / hard gate: refresh so soft UX matches server occupancy,
      // then keep the bounded error visible (load clears transient error state).
      if (isGroupTripCapacityExceededError(cause)) {
        await load();
      }
      setError(message);
    } finally {
      setBusy('');
    }
  }

  function requestHostCancel() {
    if (!trip || busy) return;
    setPendingConfirm({ kind: 'cancel' });
  }

  function requestRemoveParticipant(participantId) {
    if (!trip || busy) return;
    setPendingConfirm({ kind: 'remove', participantId: String(participantId) });
  }

  function requestRejectApplication(applicationId) {
    if (!trip || busy) return;
    setPendingConfirm({ kind: 'reject', applicationId: String(applicationId) });
  }

  function requestRevokeApproval(applicationId) {
    if (!trip || busy) return;
    setPendingConfirm({ kind: 'revoke', applicationId: String(applicationId) });
  }

  function confirmPending() {
    if (!pendingConfirm || busy || !trip) return;
    if (pendingConfirm.kind === 'cancel') {
      run('cancel', () => groupTripApi.cancel(trip.groupTripId, confirmPayload(trip.rowVersion)));
      return;
    }
    if (pendingConfirm.kind === 'remove') {
      const participantId = pendingConfirm.participantId;
      run(`remove-${participantId}`, () => groupTripApi.removeParticipant(trip.groupTripId, participantId));
      return;
    }
    if (pendingConfirm.kind === 'reject') {
      const applicationId = pendingConfirm.applicationId;
      run(`reject-${applicationId}`, () => groupTripApi.reject(trip.groupTripId, applicationId));
      return;
    }
    if (pendingConfirm.kind === 'revoke') {
      const applicationId = pendingConfirm.applicationId;
      run(`revoke-${applicationId}`, () => groupTripApi.reject(trip.groupTripId, applicationId));
    }
  }

  if (phase === 'loading') return <main className="group-trips"><p>{t('travel.common.loading')}</p></main>;
  if (phase === 'error' && !trip) {
    return (
      <main className="group-trips" role="alert">
        <p>{error}</p>
        <button type="button" onClick={load}>{t('travel.common.retry')}</button>
      </main>
    );
  }

  const approveBlockedByCapacity = isHostApproveBlockedByCapacity(trip);
  const confirmOpen = pendingConfirm != null;
  const confirmKind = pendingConfirm?.kind;
  let confirmTitle = t('travel.groupTrip.hostCancel');
  let confirmDescription = hostCancellationNotice(trip, t) || '取消後將結束此揪團招募。已付款旅伴將依規則處理退款。';
  let confirmLabel = hostCancelActionLabel(trip, t);
  if (confirmKind === 'remove') {
    confirmTitle = t('travel.groupTrip.remove');
    confirmDescription = '確定要將此旅伴移出揪團？';
    confirmLabel = t('travel.groupTrip.remove');
  } else if (confirmKind === 'reject') {
    confirmTitle = '拒絕申請';
    confirmDescription = '對方申請將被拒絕';
    confirmLabel = '拒絕申請';
  } else if (confirmKind === 'revoke') {
    confirmTitle = t('travel.groupTrip.revokeApproval');
    confirmDescription = '已核准但尚未付款的申請會被撤銷，可能影響名額/席次狀態。';
    confirmLabel = t('travel.groupTrip.revokeApproval');
  }

  return (
    <main className="group-trips">
      <header className="group-trips__intro">
        <p>{t('travel.common.host')}</p>
        <h1>{trip.recruitmentTitle}</h1>
        <span data-testid="host-group-trip-occupancy">
          {groupTripRecruitmentStatusLabel(trip.recruitmentStatus)} · {occupancyCopy(trip, t, tf)}
        </span>
        {tripPhaseCopy(trip, t, tf) ? <p className="group-trips__hint">{tripPhaseCopy(trip, t, tf)}</p> : null}
        {hostCancellationNotice(trip, t) ? (
          <p className="group-trips__hint">{hostCancellationNotice(trip, t)}</p>
        ) : (
          <p className="group-trips__hint">{t('travel.groupTrip.recruitmentHint')}</p>
        )}
        <Link to="/group-trips">{t('travel.groupTrip.backToList')}</Link>
        {trip.journeyPublicationId ? (
          <Link to={`/public/journeys/${trip.journeyPublicationId}`}>{t('travel.groupTrip.publicJourneyLink')}</Link>
        ) : null}
        <GroupTripChatButton groupTripId={trip.groupTripId} />
        <Link to={`/group-trips/${trip.groupTripId}/disruptions`}>管理行程異動</Link>
        <Link to={`/group-trips/${trip.groupTripId}/settlements`}>處理協議</Link>
        <Link to={`/group-trips/${trip.groupTripId}/actual`} data-testid="group-actual-link">團體實際行程</Link>
      </header>
      {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
      {dateConflict ? <GroupTripDateConflictNotice {...dateConflict} /> : null}
      <section className="group-trips__panel">
        <div className="group-trips__actions">
          {canHostEditGroupTrip(trip) ? (
            <Link to={`/group-trips/${trip.groupTripId}/edit`}>{t('travel.groupTrip.editDraft')}</Link>
          ) : null}
          {trip.recruitmentStatus === 'DRAFT' ? (
            <button type="button" disabled={Boolean(busy)} onClick={() => run('open', () => groupTripApi.open(trip.groupTripId, confirmPayload(trip.rowVersion)))}>{t('travel.groupTrip.openRecruitment')}</button>
          ) : null}
          {trip.recruitmentStatus === 'OPEN' ? (
            <button type="button" disabled={Boolean(busy)} onClick={() => run('close', () => groupTripApi.close(trip.groupTripId, confirmPayload(trip.rowVersion)))}>{t('travel.groupTrip.closeRecruitment')}</button>
          ) : null}
          {canHostCancelGroupTrip(trip) ? (
            <button type="button" disabled={Boolean(busy)} onClick={requestHostCancel}>
              {hostCancelActionLabel(trip, t)}
            </button>
          ) : null}
        </div>
      </section>
      <section className="group-trips__panel" data-testid="host-applications-panel">
        <h2>{t('travel.groupTrip.applications')}</h2>
        {applications.length === 0 ? <p className="group-trips__hint">{t('travel.groupTrip.noApplications')}</p> : null}
        {focusMissed ? (
          <p className="group-trips__hint" data-testid="host-application-focus-missed">
            {t('travel.groupTrip.noticeApplicationUnavailable')}
          </p>
        ) : null}
        {approveBlockedByCapacity && applications.some((item) => item.status === 'PENDING') ? (
          <p
            className="group-trips__hint group-trips__hint--capacity"
            data-testid="host-approve-capacity-hint"
          >
            {t('travel.groupTrip.approveCapacityFullHint')}
          </p>
        ) : null}
        <ul className="group-trips__list">
          {applications.map((application) => {
            const focused = focusApplicationId != null
              && String(application.applicationId) === String(focusApplicationId);
            return (
              <li
                className={`group-trips__card${focused ? ' is-notice-focus' : ''}`}
                key={application.applicationId}
                data-application-id={application.applicationId}
                data-notice-focus={focused ? 'true' : 'false'}
                ref={focused ? focusedCardRef : undefined}
              >
                <p>{groupTripMemberFacingLabel({ role: 'applicant', memberId: application.applicantMemberId })} · {groupTripApplicationStatusLabel(application.status)}</p>
                {application.applicationNote ? <p>{application.applicationNote}</p> : null}
                {application.party?.length ? (
                  <p data-testid={`host-party-${application.applicationId}`}>
                    {tf('travel.groupTrip.requestedSeats', { count: application.party.length })}
                    {' · '}
                    {application.party.map((row) => row.displayName).filter(Boolean).join('、')}
                  </p>
                ) : null}
                {application.status === 'PENDING' ? (
                  <div className="group-trips__actions group-trips__actions--host-application">
                    <button
                      type="button"
                      data-testid={`host-approve-${application.applicationId}`}
                      disabled={Boolean(busy) || approveBlockedByCapacity}
                      onClick={() => run(
                        `approve-${application.applicationId}`,
                        () => groupTripApi.approve(trip.groupTripId, application.applicationId),
                      )}
                    >
                      {t('travel.groupTrip.approve')}
                    </button>
                    <button
                      type="button"
                      data-testid={`host-reject-${application.applicationId}`}
                      disabled={Boolean(busy)}
                      onClick={() => requestRejectApplication(application.applicationId)}
                    >
                      {t('travel.groupTrip.reject')}
                    </button>
                  </div>
                ) : null}
                {canRevokeApprovedApplication(application, participants) ? (
                  <div className="group-trips__actions">
                    <button
                      type="button"
                      data-testid={`host-revoke-${application.applicationId}`}
                      disabled={Boolean(busy)}
                      onClick={() => requestRevokeApproval(application.applicationId)}
                    >
                      {t('travel.groupTrip.revokeApproval')}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
      <GroupTripLateJoinSection
        groupTripId={trip.groupTripId}
        role="host"
        viewerId={viewerId}
        hostMemberId={trip.hostMemberId}
        travelers={travelers}
        onChanged={load}
      />
      <section className="group-trips__panel" data-testid="host-traveler-roster">
        <h2>{t('travel.groupTrip.travelersHeading')}</h2>
        {travelers.length === 0 ? <p className="group-trips__hint">{t('travel.groupTrip.noTravelers')}</p> : null}
        <ul className="group-trips__list">
          {travelers.map((traveler) => (
            <li className="group-trips__card" key={traveler.travelerId}>
              <p>{traveler.displayName} · {traveler.guest ? t('travel.groupTrip.guestLabel') : t('travel.groupTrip.linkedLabel')} · {traveler.status}</p>
              {lateJoinTravelerNote(traveler, tf) ? (
                <p>{lateJoinTravelerNote(traveler, tf)}</p>
              ) : null}
              {traveler.guest && traveler.status === 'ACTIVE' ? (
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run(`claim-${traveler.travelerId}`, async () => {
                    setIssuedClaim(await groupTripApi.issueTravelerClaim(trip.groupTripId, traveler.travelerId));
                  })}
                >
                  {t('travel.groupTrip.issueClaim')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {issuedClaim?.claimToken ? (
          <p data-testid="host-traveler-claim-token">
            {t('travel.groupTrip.claimTokenHint')}
            {' '}
            <Link to={`/group-trips/claim#${encodeURIComponent(issuedClaim.claimToken)}`}>
              {t('travel.groupTrip.claimAccept')}
            </Link>
          </p>
        ) : null}
      </section>
      <GroupTripPartyAdditionSection groupTripId={trip.groupTripId} viewerId={trip.hostMemberId} travelers={travelers} isHost={true} onChanged={load} />
      <GroupTripActivityParticipation groupTripId={trip.groupTripId} />
      <GroupTripD1Participation groupTripId={trip.groupTripId} />
      <GroupTripRuntimeDecisions groupTripId={trip.groupTripId} />
      <GroupTripRuntimeTimeline groupTripId={trip.groupTripId} hostMemberId={trip.hostMemberId} />
      <section className="group-trips__panel">
        <h2>{t('travel.common.participants')}</h2>
        {participants.length === 0 ? <p className="group-trips__hint">{t('travel.groupTrip.noParticipants')}</p> : null}
        <ul className="group-trips__list">
          {participants.map((participant) => (
            <li className="group-trips__card" key={participant.participantId}>
              <p>{groupTripMemberFacingLabel({ role: 'companion', memberId: participant.memberId })} · {groupTripParticipantStatusLabel(participant.status)}</p>
              {canHostRemoveParticipant(participant) ? (
                <div className="group-trips__actions">
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => requestRemoveParticipant(participant.participantId)}
                  >
                    {t('travel.groupTrip.remove')}
                  </button>
                </div>
              ) : paidParticipantHostHint(participant, t) ? (
                <p className="group-trips__hint">{paidParticipantHostHint(participant, t)}</p>
              ) : null}
              {participant.memberId != null ? (
                <MemberSafetyActions
                  targetMemberId={participant.memberId}
                  reportTitleKey="travel.safety.reportParticipant"
                  reportLabelKey="travel.safety.reportParticipant"
                  blockLabelKey="travel.safety.blockParticipant"
                />
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        cancelLabel={t('travel.safety.cancel')}
        danger
        loading={Boolean(busy)}
        onCancel={() => {
          if (!busy) setPendingConfirm(null);
        }}
        onConfirm={confirmPending}
      />
    </main>
  );
}

/**
 * 開放招募的衝突是主揪自己的行程，可列出；核准申請的衝突屬於申請人，
 * 只顯示無法核准，不呈現對方的其他揪團（後端 args 不渲染）。
 */
function hostDateConflictNotice(kind, cause) {
  if (isGroupTripDateConflictRetryable(cause)) return { variant: 'busy' };
  if (kind === 'open') {
    return {
      variant: 'blocked',
      trips: groupTripDateConflicts(cause),
      bodyKey: 'travel.groupTrip.dateConflict.openBody',
    };
  }
  return { variant: 'applicant' };
}
